import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { getSiteDescription, replaceLegacySiteName, useContent } from "../lib/content";
import { applyTheme, THEME_KEYS } from "../lib/theme";
import { FONT_OPTIONS } from "../lib/typography";
import { toast } from "sonner";
import RichTextEditor from "./RichTextEditor";

const inputCls =
  "w-full bg-ink-900 border border-bone-50/10 px-4 py-3 focus:outline-none focus:border-ember transition-colors text-bone-50 text-sm";

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-bone-200/50 mb-2">
        {label}
      </span>
      {children}
      {hint && (
        <span className="block text-[11px] text-bone-200/40 mt-1.5">{hint}</span>
      )}
    </label>
  );
}

function SectionBlock({ title, eyebrow, children }) {
  return (
    <section className="border border-bone-50/5 p-6 md:p-8">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember mb-1">
        {eyebrow}
      </div>
      <h3 className="font-display text-2xl mb-6">{title}</h3>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

function CustomSectionsEditor({ items, onChange }) {
  const [confirmIdx, setConfirmIdx] = useState(null);

  const update = (i, key, val) => {
    const next = [...items];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };
  const add = () =>
    onChange([
      ...items,
      { anchor: "", eyebrow: "", title: "", body: "" },
    ]);
  const remove = (i) => {
    if (confirmIdx !== i) {
      setConfirmIdx(i);
      // auto-cancel after 4s
      setTimeout(() => setConfirmIdx((cur) => (cur === i ? null : cur)), 4000);
      return;
    }
    setConfirmIdx(null);
    onChange(items.filter((_, idx) => idx !== i));
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const slugifyAnchor = (s) =>
    (s || "")
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const anchors = items.map((it) => (it.anchor || "").trim());
  const dupes = new Set(
    anchors.filter((a, i) => a && anchors.indexOf(a) !== i)
  );

  return (
    <div className="space-y-4" data-testid="custom_sections">
      {items.length === 0 && (
        <div className="border border-dashed border-bone-50/10 p-6 text-center text-sm text-bone-200/50">
          No custom sections yet. Add one and link to it from a menu item via{" "}
          <code className="text-ember">/#anchor</code>.
        </div>
      )}

      {items.map((it, i) => (
        <div
          key={i}
          data-testid={`custom_sections-row-${i}`}
          className="border border-bone-50/10 bg-ink-900/40"
        >
          <div className="flex items-center justify-between px-4 py-2 border-b border-bone-50/5">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-bone-200/60">
              Block {String(i + 1).padStart(2, "0")}
              {it.title && (
                <span className="text-bone-200/40"> · {it.title}</span>
              )}
            </div>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => move(i, -1)}
                aria-label="Move up"
                className="font-mono text-[10px] px-2 py-1 border border-bone-50/15 hover:border-bone-50/40"
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => move(i, 1)}
                aria-label="Move down"
                className="font-mono text-[10px] px-2 py-1 border border-bone-50/15 hover:border-bone-50/40"
              >
                ↓
              </button>
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`custom_sections-remove-${i}`}
                className={`font-mono text-[10px] uppercase px-3 py-1 border transition-colors ${
                  confirmIdx === i
                    ? "border-red-500 text-red-500 bg-red-500/10"
                    : "border-bone-50/20 hover:border-red-500 hover:text-red-500"
                }`}
              >
                {confirmIdx === i ? "Click again to confirm" : "✕ Remove"}
              </button>
            </div>
          </div>

          <div className="p-4 space-y-3">
            <div className="grid md:grid-cols-3 gap-3">
              <input
                value={it.anchor || ""}
                onChange={(e) => update(i, "anchor", slugifyAnchor(e.target.value))}
                placeholder="anchor (e.g. resume)"
                data-testid={`custom_sections-${i}-anchor`}
                className={
                  inputCls +
                  " font-mono text-[12px] " +
                  (dupes.has(it.anchor) ? "border-red-500/60" : "")
                }
              />
              <input
                value={it.eyebrow || ""}
                onChange={(e) => update(i, "eyebrow", e.target.value)}
                placeholder="Eyebrow (optional)"
                data-testid={`custom_sections-${i}-eyebrow`}
                className={inputCls}
              />
              <input
                value={it.title || ""}
                onChange={(e) => update(i, "title", e.target.value)}
                placeholder="Section title"
                data-testid={`custom_sections-${i}-title`}
                className={inputCls}
              />
            </div>
            {dupes.has(it.anchor) && (
              <div className="text-[11px] text-red-400">
                Duplicate anchor — only the first will be used by menu links.
              </div>
            )}

            <RichTextEditor
              value={it.body || ""}
              onChange={(v) => update(i, "body", v)}
              placeholder="Body — use the toolbar above to format text or insert images."
              testId={`custom_sections-${i}-body`}
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={add}
        data-testid="custom_sections-add"
        className="font-mono text-[10px] uppercase tracking-[0.25em] border border-dashed border-bone-50/20 px-4 py-2 hover:border-ember hover:text-ember"
      >
        + Add page section
      </button>
    </div>
  );
}

/**
 * Generic repeater for arrays of {label, href} or {k, v} or strings.
 */
function Repeater({ items, onChange, fields, addLabel, testId, multiline = [] }) {
  const update = (i, key, val) => {
    const next = [...items];
    if (key == null) {
      next[i] = val;
    } else {
      next[i] = { ...next[i], [key]: val };
    }
    onChange(next);
  };
  const add = () => {
    const blank = fields ? Object.fromEntries(fields.map((f) => [f.key, ""])) : "";
    onChange([...items, blank]);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  const isMultiline = (key) => multiline.includes(key);
  const hasMultiline = fields && fields.some((f) => isMultiline(f.key));

  return (
    <div className="space-y-2" data-testid={testId}>
      {items.map((it, i) => (
        <div
          key={i}
          className={`flex items-start gap-2 ${
            hasMultiline ? "border border-bone-50/5 p-3" : ""
          }`}
          data-testid={`${testId}-row-${i}`}
        >
          {fields ? (
            <div
              className={`flex-1 grid gap-2 ${
                hasMultiline ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"
              }`}
            >
              {fields.map((f) =>
                isMultiline(f.key) ? (
                  <textarea
                    key={f.key}
                    value={it?.[f.key] ?? ""}
                    onChange={(e) => update(i, f.key, e.target.value)}
                    placeholder={f.placeholder}
                    rows={4}
                    data-testid={`${testId}-${i}-${f.key}`}
                    className={inputCls}
                  />
                ) : (
                  <input
                    key={f.key}
                    value={it?.[f.key] ?? ""}
                    onChange={(e) => update(i, f.key, e.target.value)}
                    placeholder={f.placeholder}
                    data-testid={`${testId}-${i}-${f.key}`}
                    className={inputCls}
                  />
                )
              )}
            </div>
          ) : (
            <input
              value={it ?? ""}
              onChange={(e) => update(i, null, e.target.value)}
              data-testid={`${testId}-${i}`}
              className={inputCls + " flex-1"}
            />
          )}
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => move(i, -1)}
              className="font-mono text-[10px] px-2 py-1 border border-bone-50/15 hover:border-bone-50/40"
              aria-label="Move up"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={() => move(i, 1)}
              className="font-mono text-[10px] px-2 py-1 border border-bone-50/15 hover:border-bone-50/40"
              aria-label="Move down"
            >
              ↓
            </button>
          </div>
          <button
            type="button"
            onClick={() => remove(i)}
            data-testid={`${testId}-remove-${i}`}
            className="font-mono text-[10px] uppercase px-3 py-2 border border-bone-50/20 hover:border-red-500 hover:text-red-500 self-start"
          >
            ✕
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        data-testid={`${testId}-add`}
        className="font-mono text-[10px] uppercase tracking-[0.25em] border border-dashed border-bone-50/20 px-4 py-2 hover:border-ember hover:text-ember"
      >
        {addLabel}
      </button>
    </div>
  );
}

const BUILT_IN_ANCHORS = new Set(["work", "studio"]);

function NavLinkHints({ navLinks, customSections, onAddMatchingSection }) {
  const sectionAnchors = new Set(
    (customSections || []).map((s) => (s.anchor || "").trim()).filter(Boolean)
  );
  const missing = [];
  for (const link of navLinks || []) {
    const href = (link?.href || "").trim();
    const m = href.match(/^\/?#([\w-]+)$/);
    if (!m) continue;
    const anchor = m[1];
    if (BUILT_IN_ANCHORS.has(anchor)) continue;
    if (sectionAnchors.has(anchor)) continue;
    if (!missing.includes(anchor)) missing.push(anchor);
  }
  if (missing.length === 0) return null;
  return (
    <div
      data-testid="nav-link-hints"
      className="mt-3 border border-dashed border-ember/40 bg-ember/5 p-3 space-y-2"
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ember">
        ⚠ Menu link has no destination
      </div>
      {missing.map((a) => (
        <div
          key={a}
          className="flex items-center justify-between gap-3 text-[12px] text-bone-200/70"
        >
          <span>
            Link <code className="text-ember">/#{a}</code> currently scrolls nowhere on
            the homepage.
          </span>
          <button
            type="button"
            data-testid={`add-matching-section-${a}`}
            onClick={() => onAddMatchingSection(a)}
            className="font-mono text-[10px] uppercase tracking-[0.25em] border border-ember/60 text-ember px-3 py-1.5 hover:bg-ember hover:text-ink-900"
          >
            + Add "{a}" section
          </button>
        </div>
      ))}
    </div>
  );
}

export default function SiteContentPanel() {
  const { content: serverContent, refresh } = useContent();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (serverContent) setDraft(serverContent);
  }, [serverContent]);

  if (!draft) {
    return (
      <div className="text-bone-200/50 font-mono text-xs uppercase py-20 text-center">
        Loading…
      </div>
    );
  }

  const set = (key, val) => {
    const next = { ...draft, [key]: val };
    setDraft(next);
    // Apply theme changes live so admin sees them while editing.
    if (key.startsWith("theme_")) applyTheme(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", draft);
      await refresh();
      toast.success("Site content updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset every site text back to the original defaults?")) return;
    setResetting(true);
    try {
      const res = await api.post("/content/reset");
      setDraft(res.data);
      applyTheme(res.data);
      await refresh();
      toast.success("Reset to defaults.");
    } catch (e) {
      toast.error("Failed to reset.");
    } finally {
      setResetting(false);
    }
  };

  const exportBackup = async () => {
    try {
      const [contentRes, sectionsRes, projectsRes] = await Promise.all([
        api.get("/content"),
        api.get("/sections"),
        api.get("/projects"),
      ]);
      const backup = {
        exported_at: new Date().toISOString(),
        content: contentRes.data,
        sections: sectionsRes.data,
        projects: projectsRes.data,
      };
      const blob = new Blob([JSON.stringify(backup, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `website-backup-${stamp}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup exported.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to export backup.");
    }
  };

  return (
    <div data-testid="content-panel">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <h2 className="font-display text-4xl tracking-tightest">Site content</h2>
          <p className="text-bone-200/50 text-sm mt-2">
            Edit every text and link on the public homepage.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportBackup}
            data-testid="export-backup-btn"
            className="font-mono text-[11px] uppercase tracking-[0.25em] border border-bone-50/20 px-4 py-3 hover:border-ember hover:text-ember disabled:opacity-50"
          >
            Export JSON
          </button>
          <button
            onClick={resetAll}
            disabled={resetting}
            data-testid="reset-content-btn"
            className="font-mono text-[11px] uppercase tracking-[0.25em] border border-bone-50/20 px-4 py-3 hover:border-red-500 hover:text-red-500 disabled:opacity-50"
          >
            {resetting ? "Resetting…" : "Reset defaults"}
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-content-btn"
            className="font-mono text-xs uppercase tracking-[0.3em] bg-ember text-ink-900 px-5 py-3 hover:bg-ember-light disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="grid gap-6">
        <SectionBlock eyebrow="00A / Metadata" title="Website title and description">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Website title" hint="Shown in the browser tab and search results.">
              <input
                value={draft.site_title ?? ""}
                onChange={(e) => set("site_title", e.target.value)}
                data-testid="site_title"
                className={inputCls}
                placeholder="Studio Name - Portfolio"
              />
            </Field>
            <Field label="Website description" hint="Used for the page meta description.">
              <textarea
                value={draft.site_description ?? ""}
                onChange={(e) => set("site_description", e.target.value)}
                data-testid="site_description"
                rows={4}
                className={inputCls}
                placeholder="A short description of the website."
              />
            </Field>
          </div>
        </SectionBlock>

        <SectionBlock eyebrow="00 / Theme" title="Color palette">
          <p className="text-[12px] text-bone-200/50 -mt-3 mb-3 leading-relaxed">
            Pick six base colors. Changes preview live in this tab — click <em>Save changes</em> to publish them site-wide.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {THEME_KEYS.map((t) => (
              <ThemeColorField
                key={t.key}
                label={t.label}
                value={draft[t.key] || t.default}
                onChange={(v) => set(t.key, v)}
                testIdKey={t.key}
                defaultValue={t.default}
                onResetOne={() => set(t.key, t.default)}
              />
            ))}
          </div>
          <ThemePreview content={draft} />
          <div className="grid md:grid-cols-4 gap-4 pt-2">
            <FontField
              label="Body font"
              value={draft.theme_body_font || "sans"}
              onChange={(v) => set("theme_body_font", v)}
              testId="theme_body_font"
            />
            <ScaleField
              label="Section spacing"
              value={draft.theme_spacing_scale}
              onChange={(v) => set("theme_spacing_scale", v)}
              min={70}
              max={140}
              defaultValue={100}
              testId="theme_spacing_scale"
            />
            <Field label="Button style">
              <select
                value={draft.theme_button_style || "outline"}
                onChange={(e) => set("theme_button_style", e.target.value)}
                data-testid="theme_button_style"
                className={inputCls}
              >
                <option value="outline">Outline</option>
                <option value="solid">Solid</option>
                <option value="soft">Soft</option>
              </select>
            </Field>
            <SizeField
              label="Button radius"
              value={draft.theme_button_radius}
              onChange={(v) => set("theme_button_radius", v)}
              min={0}
              max={24}
              defaultValue={0}
              testId="theme_button_radius"
            />
          </div>
        </SectionBlock>

        <SectionBlock eyebrow="00B / Motion" title="Scroll effects">
          <div className="grid md:grid-cols-3 gap-4">
            <label className="flex items-center gap-3 border border-bone-50/10 p-4">
              <input
                type="checkbox"
                checked={Boolean(draft.scroll_effect_enabled)}
                onChange={(e) => set("scroll_effect_enabled", e.target.checked)}
                data-testid="scroll_effect_enabled"
                className="h-4 w-4 accent-[#c8602c]"
              />
              <span>
                <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-bone-200/60">
                  Enable site motion
                </span>
                <span className="block text-[11px] text-bone-200/40 mt-1">
                  Applies the selected scroll effect to major homepage sections.
                </span>
              </span>
            </label>
            <Field label="Scroll mode">
              <select
                value={draft.scroll_effect_mode || "carousel3d"}
                onChange={(e) => set("scroll_effect_mode", e.target.value)}
                data-testid="scroll_effect_mode"
                className={inputCls}
              >
                <option value="carousel3d">Carousel 3D</option>
                <option value="scale">Scale</option>
                <option value="cover">Cover</option>
                <option value="page">Page</option>
              </select>
            </Field>
            <SizeField
              label="Effect intensity"
              value={draft.scroll_effect_intensity}
              onChange={(v) => set("scroll_effect_intensity", v)}
              min={0}
              max={100}
              defaultValue={42}
              testId="scroll_effect_intensity"
            />
          </div>
        </SectionBlock>

        <SectionBlock eyebrow="01" title="Menu">
          <div className="grid md:grid-cols-2 gap-5">
            <LogoUploader
              url={draft.nav_logo_image_url}
              onChange={(v) => set("nav_logo_image_url", v)}
            />
            <SiteIconUploader
              url={draft.nav_site_icon_url}
              onChange={(v) => set("nav_site_icon_url", v)}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Logo main">
              <input
                value={draft.nav_logo_main}
                onChange={(e) => set("nav_logo_main", e.target.value)}
                data-testid="nav_logo_main"
                className={inputCls}
              />
            </Field>
            <Field label="Logo sub">
              <input
                value={draft.nav_logo_sub}
                onChange={(e) => set("nav_logo_sub", e.target.value)}
                data-testid="nav_logo_sub"
                className={inputCls}
              />
            </Field>
            <Field label="Contact button label">
              <input
                value={draft.nav_contact_label}
                onChange={(e) => set("nav_contact_label", e.target.value)}
                data-testid="nav_contact_label"
                className={inputCls}
              />
            </Field>
            <Field label="Contact email">
              <input
                value={draft.nav_contact_email}
                onChange={(e) => set("nav_contact_email", e.target.value)}
                data-testid="nav_contact_email"
                className={inputCls}
              />
            </Field>
          </div>
          <Field label="Menu items" hint="Use ↑ ↓ to reorder. To make a link land on a custom block on the homepage, use /#anchor and add a matching Page section below.">
            <Repeater
              items={draft.nav_links || []}
              onChange={(v) => set("nav_links", v)}
              fields={[
                { key: "label", placeholder: "Label" },
                { key: "href", placeholder: "/#work" },
              ]}
              addLabel="+ Add menu item"
              testId="nav_links"
            />
            <NavLinkHints
              navLinks={draft.nav_links || []}
              customSections={draft.custom_sections || []}
              onAddMatchingSection={(anchor) =>
                set("custom_sections", [
                  ...(draft.custom_sections || []),
                  { anchor, eyebrow: "", title: anchor.charAt(0).toUpperCase() + anchor.slice(1), body: "" },
                ])
              }
            />
          </Field>
        </SectionBlock>

        <SectionBlock eyebrow="02" title="Top">
          <Field label="Eyebrow line">
            <input
              value={draft.hero_eyebrow}
              onChange={(e) => set("hero_eyebrow", e.target.value)}
              data-testid="hero_eyebrow"
              className={inputCls}
            />
          </Field>
          <div className="grid md:grid-cols-3 gap-5">
            <Field label="Headline line 1">
              <input
                value={draft.hero_line1}
                onChange={(e) => set("hero_line1", e.target.value)}
                data-testid="hero_line1"
                className={inputCls}
              />
              <div className="mt-3 grid gap-3">
                <SizeField
                  label="Line 1 size"
                  value={draft.typography_hero_line1_size}
                  onChange={(v) => set("typography_hero_line1_size", v)}
                  min={20}
                  max={180}
                  defaultValue={132}
                  testId="typography_hero_line1_size"
                />
                <FontField
                  value={draft.typography_hero_line1_font}
                  onChange={(v) => set("typography_hero_line1_font", v)}
                  testId="typography_hero_line1_font"
                />
              </div>
            </Field>
            <Field label="Headline line 2 (italic)">
              <input
                value={draft.hero_line2}
                onChange={(e) => set("hero_line2", e.target.value)}
                data-testid="hero_line2"
                className={inputCls}
              />
              <div className="mt-3 grid gap-3">
                <SizeField
                  label="Line 2 size"
                  value={draft.typography_hero_line2_size}
                  onChange={(v) => set("typography_hero_line2_size", v)}
                  min={20}
                  max={180}
                  defaultValue={132}
                  testId="typography_hero_line2_size"
                />
                <FontField
                  value={draft.typography_hero_line2_font}
                  onChange={(v) => set("typography_hero_line2_font", v)}
                  testId="typography_hero_line2_font"
                />
              </div>
            </Field>
            <Field label="Headline line 3">
              <div className="grid grid-cols-2 gap-2">
                <input
                  value={draft.hero_line3_pre}
                  onChange={(e) => set("hero_line3_pre", e.target.value)}
                  placeholder="rendered "
                  data-testid="hero_line3_pre"
                  className={inputCls}
                />
                <input
                  value={draft.hero_line3_accent}
                  onChange={(e) => set("hero_line3_accent", e.target.value)}
                  placeholder="in silence."
                  data-testid="hero_line3_accent"
                  className={inputCls + " text-ember"}
                />
              </div>
              <div className="mt-3 grid gap-3">
                <SizeField
                  label="Line 3 size"
                  value={draft.typography_hero_line3_size}
                  onChange={(v) => set("typography_hero_line3_size", v)}
                  min={20}
                  max={180}
                  defaultValue={132}
                  testId="typography_hero_line3_size"
                />
                <FontField
                  value={draft.typography_hero_line3_font}
                  onChange={(v) => set("typography_hero_line3_font", v)}
                  testId="typography_hero_line3_font"
                />
              </div>
            </Field>
          </div>
          <Field label="Subtext" hint="Supports rich text — select any text and use the toolbar to style or insert an image.">
            <RichTextEditor
              value={draft.hero_subtext}
              onChange={(v) => set("hero_subtext", v)}
              placeholder="Subtext below the hero headline."
              testId="hero_subtext"
            />
          </Field>
          <Field label="CTA text">
            <input
              value={draft.hero_cta}
              onChange={(e) => set("hero_cta", e.target.value)}
              data-testid="hero_cta"
              className={inputCls}
            />
          </Field>
        </SectionBlock>

        <SectionBlock eyebrow="03" title="Middle">
          <Field label="Eyebrow">
            <input
              value={draft.studio_eyebrow}
              onChange={(e) => set("studio_eyebrow", e.target.value)}
              data-testid="studio_eyebrow"
              className={inputCls}
            />
          </Field>
          <Field label="Heading">
            <div className="grid md:grid-cols-3 gap-2">
              <input
                value={draft.studio_heading_pre}
                onChange={(e) => set("studio_heading_pre", e.target.value)}
                placeholder="A practice of "
                data-testid="studio_heading_pre"
                className={inputCls}
              />
              <input
                value={draft.studio_heading_italic}
                onChange={(e) => set("studio_heading_italic", e.target.value)}
                placeholder="patience"
                data-testid="studio_heading_italic"
                className={inputCls + " italic"}
              />
              <input
                value={draft.studio_heading_post}
                onChange={(e) => set("studio_heading_post", e.target.value)}
                placeholder=", assembled one frame at a time."
                data-testid="studio_heading_post"
                className={inputCls}
              />
            </div>
            <div className="mt-3 grid gap-3 max-w-md">
              <SizeField
                label="Heading size"
                value={draft.typography_studio_heading_size}
                onChange={(v) => set("typography_studio_heading_size", v)}
                min={20}
                max={72}
                defaultValue={48}
                testId="typography_studio_heading_size"
              />
              <FontField
                value={draft.typography_studio_heading_font}
                onChange={(v) => set("typography_studio_heading_font", v)}
                testId="typography_studio_heading_font"
              />
            </div>
          </Field>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Paragraph 1" hint="Rich text.">
              <RichTextEditor
                value={draft.studio_para1}
                onChange={(v) => set("studio_para1", v)}
                placeholder="First studio paragraph."
                testId="studio_para1"
              />
            </Field>
            <Field label="Paragraph 2" hint="Rich text.">
              <RichTextEditor
                value={draft.studio_para2}
                onChange={(v) => set("studio_para2", v)}
                placeholder="Second studio paragraph."
                testId="studio_para2"
              />
            </Field>
          </div>
          <Field label="Stats" hint="Big number on top, label below.">
            <Repeater
              items={draft.studio_stats || []}
              onChange={(v) => set("studio_stats", v)}
              fields={[
                { key: "v", placeholder: "120+" },
                { key: "k", placeholder: "Projects rendered" },
              ]}
              addLabel="+ Add stat"
              testId="studio_stats"
            />
          </Field>
        </SectionBlock>

        <SectionBlock eyebrow="04" title="Work">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Archive eyebrow">
              <input
                value={draft.gallery_eyebrow ?? ""}
                onChange={(e) => set("gallery_eyebrow", e.target.value)}
                data-testid="gallery_eyebrow"
                className={inputCls}
              />
            </Field>
            <Field label="Archive heading">
              <input
                value={draft.gallery_heading ?? ""}
                onChange={(e) => set("gallery_heading", e.target.value)}
                data-testid="gallery_heading"
                className={inputCls}
              />
            </Field>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <SizeField
              label="Archive heading size"
              value={draft.typography_archive_heading_size}
              onChange={(v) => set("typography_archive_heading_size", v)}
              min={20}
              max={88}
              defaultValue={60}
              testId="typography_archive_heading_size"
            />
            <FontField
              label="Archive heading font"
              value={draft.typography_archive_heading_font}
              onChange={(v) => set("typography_archive_heading_font", v)}
              testId="typography_archive_heading_font"
            />
            <SizeField
              label="Project card title size"
              value={draft.typography_project_title_size}
              onChange={(v) => set("typography_project_title_size", v)}
              min={20}
              max={44}
              defaultValue={30}
              testId="typography_project_title_size"
            />
            <FontField
              label="Project card title font"
              value={draft.typography_project_title_font}
              onChange={(v) => set("typography_project_title_font", v)}
              testId="typography_project_title_font"
            />
          </div>
        </SectionBlock>

        <SectionBlock eyebrow="05 / Page sections" title="Custom page blocks">
          <p className="text-[12px] text-bone-200/50 -mt-3 mb-1 leading-relaxed">
            Each block renders on the homepage with an <code className="text-ember">id</code> anchor.
            Point a menu link to <code className="text-ember">/#anchor</code> to scroll the visitor here.
            The body supports rich text — bold, italic, headings, color, links, columns, and inline images.
            Leave the eyebrow and title empty when you want the body to use the full page width.
          </p>
          <CustomSectionsEditor
            items={draft.custom_sections || []}
            onChange={(v) => set("custom_sections", v)}
          />
          <div className="grid md:grid-cols-2 gap-4">
            <SizeField
              label="Custom block title size"
              value={draft.typography_custom_section_heading_size}
              onChange={(v) => set("typography_custom_section_heading_size", v)}
              min={20}
              max={72}
              defaultValue={48}
              testId="typography_custom_section_heading_size"
            />
            <FontField
              label="Custom block title font"
              value={draft.typography_custom_section_heading_font}
              onChange={(v) => set("typography_custom_section_heading_font", v)}
              testId="typography_custom_section_heading_font"
            />
          </div>
        </SectionBlock>

        <SectionBlock eyebrow="06 / Footer" title="Footer">
          <Field label="Tagline">
            <div className="grid md:grid-cols-3 gap-2">
              <input
                value={draft.footer_tagline_line1}
                onChange={(e) => set("footer_tagline_line1", e.target.value)}
                placeholder="Let's build"
                data-testid="footer_tagline_line1"
                className={inputCls}
              />
              <input
                value={draft.footer_tagline_italic}
                onChange={(e) => set("footer_tagline_italic", e.target.value)}
                placeholder="something"
                data-testid="footer_tagline_italic"
                className={inputCls + " italic"}
              />
              <input
                value={draft.footer_tagline_line2}
                onChange={(e) => set("footer_tagline_line2", e.target.value)}
                placeholder="still."
                data-testid="footer_tagline_line2"
                className={inputCls}
              />
            </div>
            <div className="mt-3 grid gap-3 max-w-md">
              <SizeField
                label="Tagline size"
                value={draft.typography_footer_tagline_size}
                onChange={(v) => set("typography_footer_tagline_size", v)}
                min={20}
                max={128}
                defaultValue={96}
                testId="typography_footer_tagline_size"
              />
              <FontField
                value={draft.typography_footer_tagline_font}
                onChange={(v) => set("typography_footer_tagline_font", v)}
                testId="typography_footer_tagline_font"
              />
            </div>
          </Field>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Address label">
              <input
                value={draft.footer_address_label}
                onChange={(e) => set("footer_address_label", e.target.value)}
                data-testid="footer_address_label"
                className={inputCls}
              />
            </Field>
            <Field label="Contact label">
              <input
                value={draft.footer_contact_label}
                onChange={(e) => set("footer_contact_label", e.target.value)}
                data-testid="footer_contact_label"
                className={inputCls}
              />
            </Field>
          </div>
          <Field label="Address lines">
            <Repeater
              items={draft.footer_address_lines || []}
              onChange={(v) => set("footer_address_lines", v)}
              fields={null}
              addLabel="+ Add address line"
              testId="footer_address_lines"
            />
          </Field>
          <Field label="Footer links" hint="Use mailto: for email links.">
            <Repeater
              items={draft.footer_links || []}
              onChange={(v) => set("footer_links", v)}
              fields={[
                { key: "label", placeholder: "Instagram ↗" },
                { key: "href", placeholder: "https://…" },
              ]}
              addLabel="+ Add footer link"
              testId="footer_links"
            />
          </Field>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Legal — left">
              <input
                value={draft.footer_legal_left}
                onChange={(e) => set("footer_legal_left", e.target.value)}
                data-testid="footer_legal_left"
                className={inputCls}
              />
            </Field>
            <Field label="Legal — right">
              <input
                value={draft.footer_legal_right}
                onChange={(e) => set("footer_legal_right", e.target.value)}
                data-testid="footer_legal_right"
                className={inputCls}
              />
            </Field>
          </div>
        </SectionBlock>
      </div>

      <div className="sticky bottom-4 mt-8 flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-content-btn-bottom"
          className="font-mono text-xs uppercase tracking-[0.3em] bg-ember text-ink-900 px-6 py-3 hover:bg-ember-light disabled:opacity-50 shadow-2xl"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}


function LogoUploader({ url, onChange }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
      toast.success("Logo updated.");
    } catch (e) {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Field
      label="Logo image"
      hint="Optional. PNG/SVG with transparent background works best. If empty, the letter mark + text below is used."
    >
      <div className="flex items-center gap-4">
        {url ? (
          <div className="relative bg-ink-900/60 border border-bone-50/10 p-2 inline-flex">
            <img
              src={url}
              alt="Site logo"
              data-testid="nav_logo_image_preview"
              className="h-12 w-auto object-contain"
            />
            <button
              type="button"
              onClick={() => onChange("")}
              data-testid="nav_logo_image_remove"
              className="absolute -top-2 -right-2 h-6 w-6 bg-ink-900 border border-bone-50/20 text-bone-50 text-xs"
              aria-label="Remove logo"
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="h-14 w-32 border border-dashed border-bone-50/15 flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.25em] text-bone-200/40">
            No logo set
          </div>
        )}
        <label
          data-testid="nav_logo_image_upload"
          className="cursor-pointer font-mono text-[10px] uppercase tracking-[0.25em] border border-bone-50/20 px-4 py-3 text-bone-200/70 hover:border-ember hover:text-ember transition-colors"
        >
          {uploading ? "Uploading…" : url ? "Replace logo" : "+ Upload logo"}
          <input
            type="file"
            accept="image/*"
            data-testid="nav_logo_image_input"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files?.[0])}
          />
        </label>
      </div>
    </Field>
  );
}

function SiteIconUploader({ url, onChange }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
      toast.success("Site icon updated.");
    } catch (e) {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Field
      label="Site icon"
      hint="Browser tab icon. Square PNG, SVG, or ICO-style image works best."
    >
      <div className="flex items-center gap-4">
        {url ? (
          <div className="relative bg-ink-900/60 border border-bone-50/10 p-2 inline-flex">
            <img
              src={url}
              alt="Site icon"
              data-testid="nav_site_icon_preview"
              className="h-10 w-10 object-contain"
            />
            <button
              type="button"
              onClick={() => onChange("")}
              data-testid="nav_site_icon_remove"
              className="absolute -top-2 -right-2 h-6 w-6 bg-ink-900 border border-bone-50/20 text-bone-50 text-xs"
              aria-label="Remove site icon"
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="h-14 w-14 border border-dashed border-bone-50/15 flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.2em] text-bone-200/40">
            Icon
          </div>
        )}
        <label
          data-testid="nav_site_icon_upload"
          className="cursor-pointer font-mono text-[10px] uppercase tracking-[0.25em] border border-bone-50/20 px-4 py-3 text-bone-200/70 hover:border-ember hover:text-ember transition-colors"
        >
          {uploading ? "Uploading…" : url ? "Replace icon" : "+ Upload icon"}
          <input
            type="file"
            accept="image/*,.ico"
            data-testid="nav_site_icon_input"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files?.[0])}
          />
        </label>
      </div>
    </Field>
  );
}


function ThemeColorField({ label, value, onChange, testIdKey, defaultValue, onResetOne }) {
  const isDefault = (value || "").toLowerCase() === defaultValue.toLowerCase();
  return (
    <div data-testid={`theme-field-${testIdKey}`} className="border border-bone-50/10 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone-200/60">
          {label}
        </span>
        {!isDefault && (
          <button
            type="button"
            onClick={onResetOne}
            data-testid={`theme-reset-${testIdKey}`}
            className="font-mono text-[9px] uppercase tracking-[0.2em] text-bone-200/40 hover:text-ember"
            title={`Reset to ${defaultValue}`}
          >
            ↺
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testIdKey}-color`}
          className="h-10 w-14 bg-transparent border border-bone-50/10 cursor-pointer p-0"
          aria-label={`${label} color`}
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testIdKey}-hex`}
          className="flex-1 bg-ink-900 border border-bone-50/10 px-3 py-2 font-mono text-[12px] text-bone-50 focus:outline-none focus:border-ember"
          placeholder="#000000"
        />
      </div>
    </div>
  );
}

function ThemePreview({ content }) {
  return (
    <div
      data-testid="theme-preview"
      className="mt-4 border border-bone-50/10 bg-ink-900 p-6 space-y-4"
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember">
        ⸺ Live preview
      </div>
      <div className="font-display text-3xl tracking-tightest text-bone-50">
        Visual identity <span className="italic text-bone-200">preview</span>,
        <br />
        tuned <span className="text-ember">live.</span>
      </div>
      <p className="text-bone-200 leading-relaxed text-sm max-w-md">
        {replaceLegacySiteName(getSiteDescription(content), content)}
      </p>
      <div className="flex flex-wrap gap-2 pt-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] bg-ember text-ink-900 px-3 py-2">
          Accent button
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] border border-bone-50/20 text-bone-200/70 px-3 py-2 bg-ink-700">
          Panel chip
        </span>
        <button
          type="button"
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-ember underline underline-offset-2"
        >
          Link sample
        </button>
      </div>
    </div>
  );
}

function SizeField({ label, value, onChange, min, max, defaultValue, testId }) {
  const numeric = Number(value);
  const safe = Number.isFinite(numeric) ? numeric : defaultValue;
  const setSafe = (next) => {
    const n = Number(next);
    if (!Number.isFinite(n)) return;
    onChange(Math.min(max, Math.max(min, n)));
  };
  return (
    <div className="border border-bone-50/10 p-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-4 mb-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone-200/60">
          {label}
        </span>
        <button
          type="button"
          onClick={() => onChange(defaultValue)}
          className="font-mono text-[9px] uppercase tracking-[0.2em] text-bone-200/40 hover:text-ember"
        >
          Reset
        </button>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          value={safe}
          onChange={(e) => setSafe(e.target.value)}
          className="flex-1 accent-[#c8602c]"
          data-testid={`${testId}-range`}
        />
        <div className="flex items-center gap-1">
          <input
            type="number"
            min={min}
            max={max}
            value={safe}
            onChange={(e) => setSafe(e.target.value)}
            className="w-20 bg-ink-900 border border-bone-50/10 px-3 py-2 font-mono text-[12px] text-bone-50 focus:outline-none focus:border-ember"
            data-testid={`${testId}-number`}
          />
          <span className="font-mono text-[10px] uppercase text-bone-200/40">
            px
          </span>
        </div>
      </div>
    </div>
  );
}

function ScaleField({ label, value, onChange, min, max, defaultValue, testId }) {
  const numeric = Number(value);
  const safe = Number.isFinite(numeric) ? numeric : defaultValue;
  const setSafe = (next) => {
    const n = Number(next);
    if (!Number.isFinite(n)) return;
    onChange(Math.min(max, Math.max(min, n)));
  };
  return (
    <div className="border border-bone-50/10 p-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-4 mb-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone-200/60">
          {label}
        </span>
        <button
          type="button"
          onClick={() => onChange(defaultValue)}
          className="font-mono text-[9px] uppercase tracking-[0.2em] text-bone-200/40 hover:text-ember"
        >
          Reset
        </button>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          value={safe}
          onChange={(e) => setSafe(e.target.value)}
          className="flex-1 accent-[#c8602c]"
          data-testid={`${testId}-range`}
        />
        <div className="flex items-center gap-1">
          <input
            type="number"
            min={min}
            max={max}
            value={safe}
            onChange={(e) => setSafe(e.target.value)}
            className="w-20 bg-ink-900 border border-bone-50/10 px-3 py-2 font-mono text-[12px] text-bone-50 focus:outline-none focus:border-ember"
            data-testid={`${testId}-number`}
          />
          <span className="font-mono text-[10px] uppercase text-bone-200/40">
            %
          </span>
        </div>
      </div>
    </div>
  );
}

function FontField({ label = "Font", value, onChange, testId }) {
  return (
    <Field label={label}>
      <select
        value={value || "display"}
        onChange={(e) => onChange(e.target.value)}
        className={inputCls}
        data-testid={testId}
      >
        {FONT_OPTIONS.map((font) => (
          <option key={font.value} value={font.value}>
            {font.label}
          </option>
        ))}
      </select>
    </Field>
  );
}
